import React from 'react';
import defaultsDeep from 'lodash/defaultsDeep';
import flow from 'lodash/fp/flow';
import pick from 'lodash/fp/pick';
import pickBy from 'lodash/fp/pickBy';
import identity from 'lodash/fp/identity';
import first from 'lodash/fp/first';
import cond from 'lodash/fp/cond';
import findKey from 'lodash/fp/findKey';
import sortBy from 'lodash/fp/sortBy';
import mapValues from 'lodash/fp/mapValues';
import toPairs from 'lodash/fp/toPairs';
import isAfter from 'date-fns/isAfter';

import { ARTICLE_TYPE_NAME_MAP } from '@api/articles/constants';
import CenteredMessage from '@atoms/CenteredMessage';

import { formatGracefully as formatDate, toDate } from './date';

export const loading = (component, isLoading, fallback = <CenteredMessage loader>Loading...</CenteredMessage>) =>
  isLoading ? fallback : component;

export const valueOrNull = (val) => val || null;

export const appendSlug = (baseUrl, slug) => `${baseUrl}/${slug}`;

const DEFAULT_ACTION_NAMES = {
  names: {
    created: 'Created',
    scheduled: 'Scheduled',
    published: 'Published',
  },
  keys: {
    created: 'created',
    published: 'published',
  },
  weights: {
    created: 2,
    modified: 3,
    published: 1,
  },
};

const getActions = ({ keys, weights }) =>
  flow(
    // pick dates
    pick(Object.values(keys)),
    // remove any falsy properties
    pickBy(identity),
    mapValues(toDate),
    // make [key, date] pair
    toPairs,
    // sort by key weight
    sortBy(([k]) => weights[findKey((kk) => kk === k, keys)]),
  );

const getLastAction = (options) =>
  flow(
    // get sorted actions by weight
    getActions(options),
    // check if withBackdatePublish option exist
    (actions) =>
      cond([
        [() => options.withBackdatePublish === true, identity],
        [() => true, (values) => sortBy([([, v]) => -v.valueOf()], values)],
      ])(actions),
    first,
  );

const makeActionInfo = (entity, [key, date], { keys, names }) => {
  let action;

  if (key === keys.published) {
    if (isAfter(toDate(date), new Date())) {
      action = names.scheduled;
    } else {
      action = names.published;
    }
  } else if (key === keys.modified) {
    action = names.modified;
  } else if (key === keys.created) {
    action = names.created;
  }

  return {
    action,
    date,
    user: (key && entity[`${key}ByName`]) || 'Anonymous',
  };
};

export const getEntityActionsInfo = (entity, _options = {}) => {
  const options = defaultsDeep({}, _options, DEFAULT_ACTION_NAMES);

  const actions = getActions(options)(entity) || [];

  return actions.map((action) => makeActionInfo(entity, action, options));
};

export const getEntityLastActionInfo = (entity, _options = {}) => {
  const options = defaultsDeep({}, _options, DEFAULT_ACTION_NAMES);

  const lastAction = getLastAction(options)(entity) || [];

  return makeActionInfo(entity, lastAction, options);
};

export const postBy = (item, { withBackdatePublish = true } = {}) => {
  const { action, date, user } = getEntityLastActionInfo(item, { withBackdatePublish });

  if (!action) {
    return '';
  }

  let text = (
    <>
      {action}{' '}
      <time dateTime={date} title={date}>
        {formatDate(date)} by {user}
      </time>
    </>
  );

  if (item.type) {
    text = (
      <>
        {text} in {ARTICLE_TYPE_NAME_MAP[item.type]}
      </>
    );
  }

  return text;
};

export const capitalize = (string) => `${string.charAt(0).toUpperCase()}${string.slice(1)}`;

export const extractKeys = (keys, obj) => keys.reduce((acc, key) => ({ ...acc, [key]: obj[key] }), {});

export const mapFieldToSelect = ({ name }, { idKey = 'id', labelKey = 'name' } = {}) => ({
  name,
  idKey: `${name}${capitalize(idKey)}`,
  labelKey: `${name}${capitalize(labelKey)}`,
});

export const makeSelectValueFromField = (selectField, data) => {
  const value = data[selectField.idKey];
  const label = data[selectField.labelKey];

  if (!value || !data) return null;

  return { value, label };
};

export const pluralize = (count, entityName, emptyLabel = `No ${entityName}s`) => {
  if (count === 1) return `1 ${entityName}`;
  if (count > 1) return `${count} ${entityName}s`;

  return emptyLabel;
};

export const hasArrayItems = (arr) => Boolean(arr?.length > 0);

export const formatNotFoundError = ({ error, deletedError = 'Deleted', loadingError = 'Error loading article' }) =>
  error.response?.status === 404 ? deletedError : loadingError;

export const formatArticleError = (error) =>
  formatNotFoundError({ error, deletedError: 'Deleted article', loadingError: 'Error loading article' });

export const formatSponsorError = (error) =>
  error.response?.status === 404 ? 'Deleted sponsor' : 'Error loading sponsor';

export const formatLanguageError = (error) =>
  error.response?.status === 404 ? 'Deleted language' : 'Error loading language';

export const getCountryByCode = (code) => {
  if (code === 'en') return 'US';
  return code?.toUpperCase();
};

export const getFullName = (object) => {
  if (object.firstName && object.lastName) return `${object.firstName} ${object.lastName}`;

  if (!object.firstName && object.lastName) return `${object.lastName}`;

  if (!object.lastName && object.firstName) return `${object.firstName}`;

  return null;
};
